import { useMemo, useState } from 'react'
import {
  PushPin,
  Folders,
  BookBookmark,
  NotePencil,
  ClipboardText,
  GraduationCap,
  Users,
  Plus,
  CaretUp,
  type Icon as PhosphorIcon,
} from '@phosphor-icons/react'

import { cn } from '@/lib/utils'
import { IconPad, type IconPadVariant } from '@/components/ui/icon-pad'
import { Separator } from '@/components/ui/separator'
import { useQuickAccessPins, QUICK_ACCESS_PIN_LIMIT } from '@/hooks/use-quick-access-pins'
import type { QuickAccessEntry, QuickAccessIconKey, QuickAccessPad } from '@/data/teacher-quick-access-catalog'

const ICON_MAP: Record<QuickAccessIconKey, PhosphorIcon> = {
  library:     Folders,
  chat:        BookBookmark,
  assignments: NotePencil,
  exams:       ClipboardText,
  resources:   GraduationCap,
  classroom:   Users,
}

const PAD_VARIANT: Record<QuickAccessPad, IconPadVariant> = {
  b: 'blue', g: 'green', n: 'neutral', p: 'purple', o: 'orange',
}

function QuickAccessRow({ entry, pinned, locked, onToggle }: {
  entry: QuickAccessEntry
  pinned: boolean
  locked: boolean
  onToggle: () => void
}) {
  const IconComp = ICON_MAP[entry.icon]
  return (
    <li className="flex items-center gap-1 rounded-[12px] bg-muted transition-colors duration-150 hover:bg-naia-sky">
      <a
        href={entry.href}
        className="flex min-w-0 flex-1 items-center gap-3 rounded-[12px] px-3 py-[11px] font-sans text-foreground no-underline outline-none focus-visible:ring-3 focus-visible:ring-ring/50"
      >
        <IconPad variant={PAD_VARIANT[entry.pad]} size="sm"><IconComp size={20} weight="fill" /></IconPad>
        <span className="flex min-w-0 flex-col leading-[1.35]">
          <span className="truncate text-[14.5px] font-bold">{entry.title}</span>
          <span className="truncate text-[12.5px] font-medium text-muted-foreground">{entry.sub}</span>
        </span>
      </a>
      <button
        type="button"
        aria-pressed={pinned}
        aria-disabled={locked}
        aria-label={pinned ? `Détacher ${entry.title} de l'accès rapide` : `Épingler ${entry.title} à l'accès rapide`}
        onClick={() => { if (!locked) onToggle() }}
        className={cn(
          'mr-1 grid size-11 shrink-0 place-items-center rounded-[10px] outline-none transition-colors duration-150',
          'focus-visible:ring-3 focus-visible:ring-ring/50',
          pinned
            ? 'text-primary hover:bg-naia-info-bg'
            : locked
              ? 'cursor-not-allowed text-naia-muted-2'
              : 'cursor-pointer text-muted-foreground hover:bg-naia-info-bg hover:text-primary',
        )}
      >
        <PushPin size={18} weight={pinned ? 'fill' : 'regular'} />
      </button>
    </li>
  )
}

export function QuickAccessList({ entries, defaultPinned, storageKey }: {
  entries: QuickAccessEntry[]
  defaultPinned: string[]
  storageKey?: string
}) {
  const { pinned, isPinned, atLimit, toggle } = useQuickAccessPins(defaultPinned, storageKey)
  const [showAll, setShowAll] = useState(false)

  const { pinnedEntries, restEntries } = useMemo(() => {
    const byId = new Map(entries.map(e => [e.id, e]))
    const kept = pinned.map(id => byId.get(id)).filter((e): e is QuickAccessEntry => e !== undefined)
    const keptIds = new Set(kept.map(e => e.id))
    return { pinnedEntries: kept, restEntries: entries.filter(e => !keptIds.has(e.id)) }
  }, [entries, pinned])

  return (
    <div className="flex flex-col gap-[10px]">
      {pinnedEntries.length > 0 ? (
        <ul className="m-0 flex list-none flex-col gap-[10px] p-0">
          {pinnedEntries.map(entry => (
            <QuickAccessRow key={entry.id} entry={entry} pinned locked={false} onToggle={() => toggle(entry.id)} />
          ))}
        </ul>
      ) : (
        <p className="m-0 text-[13.5px] font-medium text-muted-foreground">
          Aucun raccourci épinglé — choisissez vos destinations ci-dessous.
        </p>
      )}

      <button
        type="button"
        aria-expanded={showAll}
        onClick={() => setShowAll(s => !s)}
        className="flex min-h-11 cursor-pointer items-center justify-center gap-2 rounded-[12px] border border-dashed border-border bg-transparent px-3 py-2 font-sans text-[13.5px] font-semibold text-muted-foreground outline-none transition-colors duration-150 hover:bg-muted hover:text-foreground focus-visible:ring-3 focus-visible:ring-ring/50"
      >
        {showAll
          ? <><CaretUp size={16} weight="bold" /> Masquer les autres destinations</>
          : <><Plus size={16} weight="bold" /> Ajouter un raccourci ({restEntries.length})</>}
      </button>

      {showAll && (
        <div className="flex flex-col gap-[10px]">
          <span className="flex items-center gap-[11px] text-[12px] font-extrabold uppercase tracking-[0.11em] text-muted-foreground">
            Autres destinations<Separator className="flex-1" />
          </span>
          {atLimit && (
            <p role="status" className="m-0 text-[12.5px] font-medium text-muted-foreground">
              {QUICK_ACCESS_PIN_LIMIT} raccourcis épinglés au maximum — détachez-en un pour en ajouter un autre.
            </p>
          )}
          <ul className="m-0 flex list-none flex-col gap-[10px] p-0">
            {restEntries.map(entry => (
              <QuickAccessRow
                key={entry.id}
                entry={entry}
                pinned={isPinned(entry.id)}
                locked={atLimit}
                onToggle={() => toggle(entry.id)}
              />
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
